import { useEffect, useState } from "react";
import { useData } from "@/context/DataContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { deleteWithUndo } from "@/lib/undo";

const PRESETS = [
  { name: "Ochtendroutine", start: "07:00", items: [
    { title: "Opstaan", duration_minutes: 5 },
    { title: "Tanden poetsen", duration_minutes: 5 },
    { title: "Douchen", duration_minutes: 15 },
    { title: "Ontbijt", duration_minutes: 20 },
    { title: "Aankleden", duration_minutes: 10 },
    { title: "Tas inpakken", duration_minutes: 5 },
    { title: "Vertrekken", duration_minutes: 5 },
  ]},
  { name: "Avondroutine", start: "21:30", items: [
    { title: "Opruimen", duration_minutes: 15 },
    { title: "Plan morgen", duration_minutes: 10 },
    { title: "Huidverzorging", duration_minutes: 10 },
    { title: "Lezen", duration_minutes: 20 },
    { title: "Slapen", duration_minutes: 5 },
  ]},
  { name: "Sportroutine", start: "17:00", items: [
    { title: "Warming-up", duration_minutes: 10 },
    { title: "Krachttraining", duration_minutes: 40 },
    { title: "Cardio", duration_minutes: 15 },
    { title: "Rekken", duration_minutes: 10 },
  ]},
  { name: "Boodschappen doen", start: "18:00", items: [
    { title: "Lijst maken", duration_minutes: 10 },
    { title: "Naar winkel", duration_minutes: 10 },
    { title: "Winkelen", duration_minutes: 30 },
    { title: "Uitpakken", duration_minutes: 15 },
  ]},
  { name: "Schoonmaakroutine", start: "10:00", items: [
    { title: "Keuken", duration_minutes: 20 },
    { title: "Badkamer", duration_minutes: 15 },
    { title: "Woonkamer", duration_minutes: 15 },
    { title: "Was doen", duration_minutes: 10 },
  ]},
];

export default function RoutinesPage() {
  const { routines, createRoutine, deleteRoutine, restoreRoutine, categories } = useData();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [start, setStart] = useState("07:00");
  const [categoryId, setCategoryId] = useState("");
  const [items, setItems] = useState([{ _key: crypto.randomUUID(), title: "", duration_minutes: 10 }]);

  const reset = () => {
    setName(""); setStart("07:00"); setCategoryId("");
    setItems([{ _key: crypto.randomUUID(), title: "", duration_minutes: 10 }]);
  };

  const addItem = () => setItems([...items, { _key: crypto.randomUUID(), title: "", duration_minutes: 10 }]);
  const updateItem = (i, patch) => setItems(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!name.trim()) return toast.error("Naam is vereist");
    const clean = items.filter((i) => i.title.trim()).map((i) => ({
      id: crypto.randomUUID(),
      title: i.title,
      duration_minutes: Number(i.duration_minutes) || 10,
    }));
    if (clean.length === 0) return toast.error("Voeg minstens één stap toe");
    await createRoutine({
      name, default_start: start, category_id: categoryId || null, items: clean,
    });
    toast.success("Routine aangemaakt");
    reset();
    setOpen(false);
  };

  const seedPresets = async () => {
    let added = 0;
    for (const p of PRESETS) {
      const exists = routines.some((r) => r.name === p.name);
      if (exists) continue;
      await createRoutine({
        name: p.name,
        default_start: p.start,
        category_id: null,
        items: p.items.map((i) => ({ id: crypto.randomUUID(), ...i })),
      });
      added++;
    }
    if (added > 0) toast.success(`${added} voorbeeldroutine${added === 1 ? "" : "s"} toegevoegd`);
    else toast.info("Alle voorbeeldroutines bestaan al");
  };

  return (
    <div className="p-5 lg:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Herbruikbaar</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-50 mt-1">Routines</h1>
          <div className="text-sm text-slate-500 mt-1">Eén keer bouwen, elke dag inzetten.</div>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="routines-seed-presets"
            variant="outline"
            onClick={seedPresets}
            className="border-slate-800 bg-[#121C2F] text-slate-200 hover:bg-slate-800 gap-2"
          >
            <Lightning size={16} /> Voorbeeldroutines toevoegen
          </Button>
          <Button
            data-testid="routines-new"
            onClick={() => setOpen(true)}
            className="bg-blue-600 hover:bg-blue-500 gap-2"
          >
            <Plus size={16} /> Nieuwe routine
          </Button>
        </div>
      </div>

      {routines.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-800 bg-[#121C2F]/50 p-12 text-center">
          <div className="text-slate-100 font-medium">Nog geen routines</div>
          <div className="text-sm text-slate-500 mt-1">Maak je eigen routine of laad de voorbeelden in.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {routines.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-800 bg-[#121C2F] p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-slate-100 font-semibold">{r.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Start om {r.default_start || "07:00"} · {r.items?.length || 0} stappen</div>
                </div>
                <button
                  data-testid={`routine-delete-${r.id}`}
                  onClick={() => deleteWithUndo({
                    label: `"${r.name}" verwijderd`,
                    deleteFn: async () => { await deleteRoutine(r.id); },
                    restoreFn: async () => { await restoreRoutine(r); },
                  })}
                  className="text-slate-500 hover:text-rose-400"
                >
                  <Trash size={14} />
                </button>
              </div>
              <div className="mt-3 space-y-1.5">
                {(r.items || []).map((it) => (
                  <div key={it.id} className="flex items-center justify-between text-sm">
                    <span className="text-slate-300 truncate">{it.title}</span>
                    <span className="text-xs text-slate-500 shrink-0">{it.duration_minutes}m</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121C2F] border-slate-800 text-slate-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-50">Nieuwe routine</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Naam</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                  data-testid="routine-name-input"
                  className="mt-1.5 bg-slate-900 border-slate-800" placeholder="Ochtendroutine" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Standaardstart</Label>
                <Input type="time" value={start} onChange={(e) => setStart(e.target.value)}
                  className="mt-1.5 bg-slate-900 border-slate-800" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Categorie</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger className="mt-1.5 bg-slate-900 border-slate-800"><SelectValue placeholder="Optioneel" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />{c.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-slate-400">Stappen</Label>
                <Button size="sm" variant="ghost" onClick={addItem} className="text-blue-400 hover:text-blue-300 h-7">
                  <Plus size={12} /> Stap toevoegen
                </Button>
              </div>
              <div className="space-y-2 mt-2 max-h-72 overflow-y-auto pr-1">
                {items.map((it, i) => (
                  <div key={it._key || (it._key = crypto.randomUUID())} className="flex gap-2 items-center">
                    <Input value={it.title} onChange={(e) => updateItem(i, { title: e.target.value })}
                      placeholder={`Stap ${i + 1}`}
                      className="bg-slate-900 border-slate-800 text-slate-100" />
                    <Input type="number" min={1} value={it.duration_minutes}
                      onChange={(e) => updateItem(i, { duration_minutes: e.target.value })}
                      className="bg-slate-900 border-slate-800 text-slate-100 w-24" />
                    <button onClick={() => removeItem(i)} className="text-slate-500 hover:text-rose-400 p-1">
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-100 hover:bg-slate-800/60">Annuleren</Button>
            <Button data-testid="routine-save" onClick={submit} className="bg-blue-600 hover:bg-blue-500">Routine opslaan</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
